import React from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
type ResolvedMode = 'light' | 'dark';

const STORAGE_KEY = 'corepilot.theme';

type ColorModeContextValue = {
  mode: ThemeMode;
  resolvedMode: ResolvedMode;
  setMode: (mode: ThemeMode) => void;
};

const ColorModeContext = React.createContext<ColorModeContextValue | null>(null);

function getSystemPreference(): ResolvedMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyClass(resolved: ResolvedMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export function ColorModeProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const [mode, setModeState] = React.useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    return stored ?? 'light';
  });

  const [systemMode, setSystemMode] = React.useState<ResolvedMode>(getSystemPreference);

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setSystemMode(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedMode: ResolvedMode = mode === 'system' ? systemMode : mode;

  React.useEffect(() => {
    applyClass(resolvedMode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode, resolvedMode]);

  const value = React.useMemo<ColorModeContextValue>(
    () => ({ mode, resolvedMode, setMode: setModeState }),
    [mode, resolvedMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode(): ColorModeContextValue {
  const ctx = React.useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used within ColorModeProvider');
  return ctx;
}
